import { useState, useEffect, useCallback } from "react";
import { nip19 } from "nostr-tools";
import { ExternalLink, Zap, Globe, Code, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useProfile } from "@/hooks/useProfile";
import { useAccount } from "@/hooks/useAccount";
import { useToast } from "@/hooks/useToast";
import type { NostrEvent } from "nostr-tools";
import { cn } from "@/lib/utils";

interface OEmbedData {
  title?: string;
  description?: string;
  thumbnail_url?: string;
  author_name?: string;
  provider_name?: string;
  html?: string;
  url?: string;
  width?: number;
  height?: number;
}

interface NsiteCardProps {
  event: NostrEvent;
  gateway: string;
  className?: string;
  /** Source event (zap or manifest) */
  sourceKind?: "zap" | "manifest";
  zapperPubkey?: string;
}

/**
 * Encode a 32-byte pubkey (hex) as base36, zero-padded to exactly 50 characters.
 * As specified in NIP-5A: lowercase digits 0-9 then letters a-z, no padding beyond natural length.
 */
function pubkeyToBase36(pubkeyHex: string): string {
  // Convert hex string to BigInt, then to base36
  const n = BigInt("0x" + pubkeyHex);
  const b36 = n.toString(36).toLowerCase();
  // Pad to 50 characters
  return b36.padStart(50, "0");
}

/**
 * Build the canonical nsite URL as per NIP-5A subdomain format.
 *
 * Root site  (kind 15128, no d tag): <npub>.<gateway>
 * Named site (kind 35128, with d tag): <pubkeyB36><dTag>.<gateway>
 */
function buildNsiteUrl(gateway: string, pubkeyHex: string, npub: string, dTag?: string): string {
  const host = gateway.startsWith("http") ? new URL(gateway).host : gateway;
  if (dTag && dTag !== "") {
    // Named site: 50-char base36 pubkey + dTag as subdomain
    const pubkeyB36 = pubkeyToBase36(pubkeyHex);
    return `https://${pubkeyB36}${dTag}.${host}`;
  }
  // Root site: npub as subdomain
  return `https://${npub}.${host}`;
}

function getTagValue(event: NostrEvent, tagName: string): string | undefined {
  return event.tags.find(([t]) => t === tagName)?.[1];
}

export function NsiteCard({ event, gateway, className, sourceKind, zapperPubkey }: NsiteCardProps) {
  const account = useAccount();
  const { toast } = useToast();
  const [oembed, setOembed] = useState<OEmbedData | null>(null);
  const [oembedLoading, setOembedLoading] = useState(true);
  const [oembedError, setOembedError] = useState(false);
  const [zapAmount, setZapAmount] = useState<number | null>(null);
  const [isZapping, setIsZapping] = useState(false);
  const [hasZapped, setHasZapped] = useState(false);
  const [imageError, setImageError] = useState(false);

  const authorPubkey = event.pubkey;
  const dTag = getTagValue(event, "d");
  const titleTag = getTagValue(event, "title");
  const descriptionTag = getTagValue(event, "description");
  const sourceTag = getTagValue(event, "source");

  // Build author npub
  const authorNpub = nip19.npubEncode(authorPubkey);

  // Build nsite URL using correct NIP-5A subdomain format
  const nsiteUrl = buildNsiteUrl(gateway, authorPubkey, authorNpub, dTag);

  // Load author profile
  const profile = useProfile(authorPubkey);

  // Also load zapper profile if provided
  const zapperProfile = useProfile(zapperPubkey ?? undefined);

  // Fetch OEmbed preview
  useEffect(() => {
    let cancelled = false;
    setOembedLoading(true);
    setOembedError(false);
    setOembed(null);
    setImageError(false);

    const fetchOEmbed = async () => {
      try {
        const encoded = encodeURIComponent(nsiteUrl);
        const apiUrl = `https://ditto.pub/api/link-preview/${encoded}`;
        const res = await fetch(apiUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: OEmbedData = await res.json();
        if (!cancelled) {
          setOembed(data);
        }
      } catch (err) {
        if (!cancelled) {
          setOembedError(true);
        }
      } finally {
        if (!cancelled) {
          setOembedLoading(false);
        }
      }
    };

    fetchOEmbed();
    return () => { cancelled = true; };
  }, [nsiteUrl]);

  const handleZap = useCallback(async () => {
    if (!account) {
      toast({
        title: "Login required",
        description: "Please log in to zap this nsite.",
        variant: "destructive",
      });
      return;
    }

    setIsZapping(true);
    try {
      // Build a NIP-57 zap request (kind 9734)
      // We reference the nsite event with 'e' and 'p' tags
      const amountMillisats = (zapAmount ?? 21) * 1000;

      // Get the author's lightning address from their profile
      const lud16 = profile?.lud16 ?? profile?.lud06;

      if (!lud16) {
        toast({
          title: "No lightning address",
          description: "This user doesn't have a lightning address set up.",
          variant: "destructive",
        });
        setIsZapping(false);
        return;
      }

      // Build the zap request event (NIP-57 kind 9734)
      const zapRequest = {
        kind: 9734,
        content: `⚡ Zapping nsite: ${titleTag ?? dTag ?? "root site"}`,
        tags: [
          ["p", authorPubkey],
          ["e", event.id],
          ["amount", amountMillisats.toString()],
          ["relays", "wss://relay.ditto.pub", "wss://relay.damus.io"],
        ],
        created_at: Math.floor(Date.now() / 1000),
      };

      // Resolve lud16 to LNURL endpoint
      let lnurlEndpoint: string;
      if (lud16.includes("@")) {
        const [name, domain] = lud16.split("@");
        lnurlEndpoint = `https://${domain}/.well-known/lnurlp/${name}`;
      } else {
        // It's a raw LNURL — we'd need to decode it, skip for now
        toast({
          title: "Unsupported lightning address format",
          description: "Only NIP-05 style lightning addresses are supported.",
          variant: "destructive",
        });
        setIsZapping(false);
        return;
      }

      // Fetch LNURL pay info
      const lnurlRes = await fetch(lnurlEndpoint);
      if (!lnurlRes.ok) throw new Error("Failed to fetch LNURL info");
      const lnurlData = await lnurlRes.json();

      if (!lnurlData.allowsNostr || !lnurlData.nostrPubkey) {
        toast({
          title: "Zaps not supported",
          description: "This user's lightning address doesn't support Nostr zaps.",
          variant: "destructive",
        });
        setIsZapping(false);
        return;
      }

      // Sign the zap request
      const signedZapRequest = await account.signer.signEvent(zapRequest);
      const zapRequestEncoded = encodeURIComponent(JSON.stringify(signedZapRequest));

      // Request invoice
      const callbackUrl = `${lnurlData.callback}?amount=${amountMillisats}&nostr=${zapRequestEncoded}&lnurl=${encodeURIComponent(lnurlEndpoint)}`;
      const invoiceRes = await fetch(callbackUrl);
      if (!invoiceRes.ok) throw new Error("Failed to get invoice");
      const invoiceData = await invoiceRes.json();

      if (invoiceData.status === "ERROR") {
        throw new Error(invoiceData.reason ?? "Invoice error");
      }

      const paymentRequest = invoiceData.pr;
      if (!paymentRequest) throw new Error("No payment request in response");

      // Try to pay using WebLN
      if (typeof window !== "undefined" && (window as Window & { webln?: { enable: () => Promise<void>; sendPayment: (pr: string) => Promise<unknown> } }).webln) {
        const webln = (window as Window & { webln?: { enable: () => Promise<void>; sendPayment: (pr: string) => Promise<unknown> } }).webln!;
        await webln.enable();
        await webln.sendPayment(paymentRequest);
        setHasZapped(true);
        toast({
          title: "Zap sent! ⚡",
          description: `You zapped ${zapAmount ?? 21} sats to this nsite.`,
        });
      } else {
        // Fallback: copy invoice to clipboard
        await navigator.clipboard.writeText(paymentRequest);
        toast({
          title: "Invoice copied",
          description: "No WebLN wallet found. The lightning invoice has been copied to your clipboard.",
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to zap";
      toast({
        title: "Zap failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsZapping(false);
    }
  }, [account, authorPubkey, event.id, profile, titleTag, dTag, zapAmount, toast]);

  const displayTitle = oembed?.title ?? titleTag ?? (dTag ? `${dTag}` : "Root Site");
  const displayDescription = oembed?.description ?? descriptionTag ?? "";
  const thumbnail = !imageError ? (oembed?.thumbnail_url) : null;
  const authorName = profile?.displayName ?? profile?.name ?? authorNpub.slice(0, 12) + "...";
  const authorAvatar = profile?.picture;

  return (
    <Card
      className={cn(
        "group relative overflow-hidden transition-all duration-300",
        "border-border/50 hover:border-primary/30 bg-card",
        "hover:shadow-lg hover:shadow-primary/5",
        "animate-fade-in-up",
        className,
      )}
    >
      {/* Source badge */}
      {sourceKind && (
        <div className="absolute top-3 right-3 z-10">
          {sourceKind === "zap" ? (
            <Badge
              variant="secondary"
              className="bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-medium"
            >
              <Zap className="w-3 h-3 mr-1" />
              Zapped
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="bg-primary/10 text-primary border border-primary/20 text-xs font-medium"
            >
              <Globe className="w-3 h-3 mr-1" />
              Published
            </Badge>
          )}
        </div>
      )}

      {/* Thumbnail */}
      {oembedLoading ? (
        <Skeleton className="w-full h-40 rounded-none rounded-t-lg" />
      ) : thumbnail ? (
        <div className="relative overflow-hidden h-40">
          <img
            src={thumbnail}
            alt={displayTitle}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
        </div>
      ) : (
        <div className="w-full h-40 bg-gradient-to-br from-primary/10 via-accent/5 to-muted flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Globe className="w-10 h-10 opacity-30" />
            <span className="text-xs opacity-50">No preview available</span>
          </div>
        </div>
      )}

      <CardContent className="p-4 space-y-3">
        {/* Title */}
        <div>
          {oembedLoading ? (
            <div className="space-y-1.5">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-3.5 w-full" />
            </div>
          ) : (
            <>
              <h3 className="font-semibold text-base leading-snug text-foreground line-clamp-1">
                {displayTitle}
              </h3>
              {displayDescription && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                  {displayDescription}
                </p>
              )}
            </>
          )}
        </div>

        {/* Author row */}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <div className="flex items-center gap-2 min-w-0">
            {/* If zapped, show zapper first, then author */}
            {sourceKind === "zap" && zapperPubkey ? (
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="relative">
                  <Avatar className="h-7 w-7 ring-1 ring-amber-500/30">
                    <AvatarImage src={zapperProfile?.picture} />
                    <AvatarFallback className="bg-amber-500/10 text-amber-400 text-xs">
                      {(zapperProfile?.name ?? "?")[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <Zap className="absolute -bottom-0.5 -right-0.5 w-3 h-3 text-amber-400 fill-amber-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate leading-none">
                    {zapperProfile?.displayName ?? zapperProfile?.name ?? "Someone"}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-none mt-0.5">zapped</p>
                </div>
                <span className="text-muted-foreground/40 text-xs mx-1">→</span>
                <Avatar className="h-6 w-6 ring-1 ring-primary/20 flex-shrink-0">
                  <AvatarImage src={authorAvatar} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                    {(profile?.name ?? authorName)[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 min-w-0">
                <Avatar className="h-7 w-7 ring-1 ring-primary/20">
                  <AvatarImage src={authorAvatar} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                    {authorName[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <p className="text-xs font-medium text-foreground truncate">{authorName}</p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {sourceTag && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a href={sourceTag} target="_blank" rel="noopener noreferrer">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                      <Code className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                </TooltipTrigger>
                <TooltipContent>View source</TooltipContent>
              </Tooltip>
            )}

            {/* Zap button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant={hasZapped ? "default" : "outline"}
                  className={cn(
                    "h-7 px-2.5 text-xs font-medium gap-1.5 transition-all",
                    hasZapped
                      ? "bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30"
                      : "border-border/60 text-muted-foreground hover:text-amber-400 hover:border-amber-500/40 hover:bg-amber-500/5",
                    !account && "opacity-50 cursor-not-allowed",
                  )}
                  onClick={handleZap}
                  disabled={isZapping || !account}
                >
                  {isZapping ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Zap className={cn("h-3.5 w-3.5", hasZapped && "fill-amber-400")} />
                  )}
                  <span>{hasZapped ? "Zapped" : "Zap"}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {account ? `Zap 21 sats to this nsite` : "Log in to zap"}
              </TooltipContent>
            </Tooltip>

            {/* Visit site button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <a href={nsiteUrl} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="sm"
                    variant="default"
                    className="h-7 px-2.5 text-xs font-medium gap-1.5 bg-primary/80 hover:bg-primary"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span>Visit</span>
                  </Button>
                </a>
              </TooltipTrigger>
              <TooltipContent>Open nsite</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* nsite URL */}
        <div className="flex items-center gap-1.5 pt-0.5 border-t border-border/40">
          <Globe className="h-3 w-3 text-muted-foreground/60 flex-shrink-0" />
          <a
            href={nsiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-muted-foreground/70 hover:text-primary truncate transition-colors"
          >
            {nsiteUrl.replace(/^https?:\/\//, "")}
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
